import { Editor } from "@hugerte/hugerte-react";
import { htmlsEquivalent } from "../../../utils/htmlsEquivalent";
import { usePrimaryColor } from "../../../branding/usePrimaryColor";

interface Props {
  value: string;
  onChange: (next: string) => void;
}

/**
 * HugeRTE WYSIWYG for the post body. Toolbar mirrors the admin's
 * BlogPost editor so existing posts render identically after a save.
 * Inline image uploads hit `/image_upload` (proxied to hd-api),
 * which writes to the shared blog-assets bucket and returns a CDN url.
 */
export function BlogBodyEditor({ value, onChange }: Props) {
  const primaryColor = usePrimaryColor();

  return (
    <Editor
      value={value || ""}
      onEditorChange={(content: string) => {
        if (htmlsEquivalent(content, value || "")) return;
        onChange(content);
      }}
      init={{
        height: "100%",
        menubar: true,
        plugins: [
          "lists",
          "table",
          "link",
          "image",
          "code",
          "fullscreen",
          "searchreplace",
          "wordcount",
        ],
        toolbar_mode: "sliding",
        toolbar:
          "undo redo | blocks | bold italic underline strikethrough | " +
          "alignleft aligncenter alignright alignjustify | " +
          "bullist numlist outdent indent | link image table | " +
          "code fullscreen",
        // Inline image upload — same endpoint reflex-admin uses.
        images_upload_handler: (blobInfo: {
          blob: () => Blob;
          filename: () => string;
        }) =>
          new Promise<string>((resolve, reject) => {
            const formData = new FormData();
            formData.append("file", blobInfo.blob(), blobInfo.filename());
            fetch("/image_upload", {
              method: "POST",
              credentials: "include",
              body: formData,
            })
              .then((res) => {
                if (!res.ok) throw new Error("Upload failed");
                return res.json();
              })
              .then((data: { location: string }) => resolve(data.location))
              .catch((err) => reject(err.message));
          }),
        automatic_uploads: true,
        content_style:
          "body { font-family: 'Inter', system-ui, -apple-system, sans-serif; font-size: 16px; line-height: 1.6; max-width: 760px; margin: 0 auto; padding: 16px; color: #0B2545; } " +
          "h1, h2, h3 { font-family: 'Fraunces', Georgia, serif; letter-spacing: -0.015em; } " +
          "h1 { font-size: 28px; font-weight: 500; margin: 24px 0 12px; } " +
          "h2 { font-size: 22px; font-weight: 500; margin: 20px 0 10px; } " +
          "h3 { font-size: 18px; font-weight: 500; margin: 16px 0 8px; } " +
          "p { margin: 0 0 14px; } " +
          `a { color: ${primaryColor}; }`,
        // Preserve author-entered link URLs as typed (e.g. "/contact").
        // HugeRTE's default rewrites them to bare relative paths that
        // break on multi-segment public routes like /legal-insights/:slug.
        convert_urls: false,
        promotion: false,
        branding: false,
        license_key: "gpl",
      }}
    />
  );
}
